/**
 * Archetype / template lookup for OET compilation and ADL2 template flattening.
 */

import { parseAdl, type ParseAdlResult } from "../parse_adl.ts";
import {
  type ArchetypeResolver,
  flattenToOperationalTemplate,
} from "../../am/util/flattening/template_flattener.ts";
import { readTemplateId } from "../../am/util/ontology_merge.ts";
import { isOptXml } from "./opt_xml_parser.ts";
import { isOetXml } from "./oet_xml_parser.ts";
import { isTemplateJson, parseTemplateJson } from "./template_json_parser.ts";
import type * as openehr_am from "../../am/openehr_am.ts";

export interface ArchetypeRepositoryOptions {
  /** Directory containing `.adl` / `.adls` files (searched recursively). */
  rootDir: string;
}

export type RepositoryFileKind =
  | "archetype"
  | "template"
  | "operational_template"
  | "oet_xml"
  | "opt_xml"
  | "template_json"
  | "skipped"
  | "error";

export interface LoadFileResult {
  path: string;
  kind: RepositoryFileKind;
  archetypeId?: string;
  message?: string;
}

export class ArchetypeRepository implements ArchetypeResolver {
  private byId = new Map<string, openehr_am.ARCHETYPE>();
  private templates = new Map<string, openehr_am.TEMPLATE>();
  private operational = new Map<string, openehr_am.OPERATIONAL_TEMPLATE>();
  private warnings: string[] = [];
  private loadedPaths: string[] = [];

  static async fromDirectory(rootDir: string): Promise<ArchetypeRepository> {
    const repo = new ArchetypeRepository();
    await repo.loadDirectory(rootDir);
    return repo;
  }

  static fromEntries(
    entries: Array<{ path: string; content: string }>,
  ): ArchetypeRepository {
    const repo = new ArchetypeRepository();
    for (const { path, content } of entries) {
      repo.loadFile(path, content);
    }
    return repo;
  }

  getWarnings(): string[] {
    return [...this.warnings];
  }

  listLoadedPaths(): string[] {
    return [...this.loadedPaths];
  }

  /**
   * One id per loaded template. Alias keys (`template_id`, basename,
   * version-stripped id) point at the same object and are not extra templates.
   */
  listTemplateIds(): string[] {
    const ids: string[] = [];
    for (const template of this.listTemplates()) {
      const id = template.archetype_id?.value ?? templateIdOf(template);
      if (id) ids.push(id);
    }
    return ids.sort();
  }

  /**
   * Each loaded template once. Alias keys (`template_id`, basename) are not
   * extra templates. Two Better templates that share an archetype id are both
   * returned.
   */
  listTemplates(): openehr_am.TEMPLATE[] {
    const seen = new Set<openehr_am.TEMPLATE>();
    const templates: openehr_am.TEMPLATE[] = [];
    for (const template of this.templates.values()) {
      if (seen.has(template)) continue;
      seen.add(template);
      templates.push(template);
    }
    return templates;
  }

  listOperationalIds(): string[] {
    return [...this.operational.keys()].sort();
  }

  getTemplate(templateId: string): openehr_am.TEMPLATE | undefined {
    return this.templates.get(templateId) ??
      this.templates.get(templateId.replace(/\.v[\d.]+$/, ""));
  }

  getOperationalTemplate(
    id: string,
  ): openehr_am.OPERATIONAL_TEMPLATE | undefined {
    return this.operational.get(id) ??
      this.operational.get(id.replace(/\.v[\d.]+$/, ""));
  }

  /** Flatten an ADL2 source template using archetypes in this repository. */
  flattenTemplate(
    template: openehr_am.TEMPLATE,
  ): openehr_am.OPERATIONAL_TEMPLATE {
    return flattenToOperationalTemplate(template, this);
  }

  resolve(archetypeId: string): openehr_am.ARCHETYPE | undefined {
    // A nested Better template is referenced by template id, not archetype id.
    return this.get(archetypeId) ?? this.getTemplate(archetypeId);
  }

  async loadDirectory(rootDir: string): Promise<void> {
    for await (const entry of Deno.readDir(rootDir)) {
      const path = `${rootDir}/${entry.name}`;
      if (entry.isDirectory) {
        await this.loadDirectory(path);
        continue;
      }
      if (!/\.(adl|adls|opt|oet|t\.json|xml)$/i.test(entry.name)) continue;
      try {
        const text = await Deno.readTextFile(path);
        this.loadFile(path, text);
      } catch (e) {
        this.warnings.push(`Failed ${path}: ${(e as Error).message}`);
      }
    }
  }

  loadFile(path: string, text: string): LoadFileResult {
    this.loadedPaths.push(path);
    try {
      const trimmed = text.trim();
      if (!trimmed) {
        return { path, kind: "skipped", message: "empty file" };
      }

      if (
        trimmed.startsWith("{") &&
        (isTemplateJson(trimmed) || /\.t\.json$/i.test(path))
      ) {
        return this.ingestTemplateJson(path, trimmed);
      }

      if (trimmed.startsWith("<?xml") || trimmed.startsWith("<")) {
        if (isOetXml(trimmed)) {
          return {
            path,
            kind: "oet_xml",
            message: "OET XML (compiled at generation root)",
          };
        }
        if (isOptXml(trimmed)) {
          return {
            path,
            kind: "opt_xml",
            message: "OPT XML (parsed at generation root)",
          };
        }
        return { path, kind: "skipped", message: "unrecognized XML" };
      }

      const parsed = parseAdl(text, { convertAdl14: true });
      return this.ingestParseResult(path, parsed);
    } catch (e) {
      const message = (e as Error).message;
      this.warnings.push(`Failed ${path}: ${message}`);
      return { path, kind: "error", message };
    }
  }

  private ingestTemplateJson(path: string, text: string): LoadFileResult {
    const { template, overlays, warnings } = parseTemplateJson(text);
    for (const overlay of overlays) {
      this.add(overlay);
    }
    this.indexTemplate(template, path);
    for (const w of warnings) {
      this.warnings.push(`${path}: ${w}`);
    }
    const id = template.archetype_id?.value ?? templateIdOf(template) ?? path;
    return { path, kind: "template_json", archetypeId: id };
  }

  private ingestParseResult(
    path: string,
    parsed: ParseAdlResult,
  ): LoadFileResult {
    if (parsed.kind === "archetype" && parsed.archetype) {
      this.add(parsed.archetype);
      const id = parsed.archetype.archetype_id?.value ?? path;
      return { path, kind: "archetype", archetypeId: id };
    }
    if (parsed.kind === "template" && parsed.template) {
      this.indexTemplate(parsed.template, path);
      const id = parsed.template.archetype_id?.value ?? path;
      return { path, kind: "template", archetypeId: id };
    }
    if (parsed.kind === "operational_template" && parsed.operationalTemplate) {
      const id = parsed.operationalTemplate.archetype_id?.value ?? path;
      this.operational.set(id, parsed.operationalTemplate);
      const base = id.replace(/\.v[\d.]+$/, "");
      if (!this.operational.has(base)) {
        this.operational.set(base, parsed.operationalTemplate);
      }
      return { path, kind: "operational_template", archetypeId: id };
    }
    return {
      path,
      kind: "skipped",
      message: `unsupported kind: ${parsed.kind}`,
    };
  }

  /**
   * Index a source template by archetype id, Better `template_id`, and the
   * file basename (`ChemoQ-fatigue.t.json` → `ChemoQ-fatigue`).
   */
  private indexTemplate(template: openehr_am.TEMPLATE, path: string): void {
    const id = template.archetype_id?.value ?? path;
    const keys = new Set<string>([id]);
    const templateId = templateIdOf(template);
    if (templateId) keys.add(templateId);
    const baseName = templateBasename(path);
    if (baseName) keys.add(baseName);
    for (const key of keys) this.rememberTemplate(key, template);
  }

  private rememberTemplate(id: string, template: openehr_am.TEMPLATE): void {
    this.templates.set(id, template);
    const base = id.replace(/\.v[\d.]+$/, "");
    if (base && !this.templates.has(base)) this.templates.set(base, template);
  }

  add(archetype: openehr_am.ARCHETYPE): void {
    const id = archetype.archetype_id?.value;
    if (id) this.byId.set(id, archetype);
    const base = id?.replace(/\.v[\d.]+$/, "");
    if (base && !this.byId.has(base)) this.byId.set(base, archetype);
  }

  get(archetypeId: string): openehr_am.ARCHETYPE | undefined {
    return this.byId.get(archetypeId) ??
      this.byId.get(archetypeId.replace(/\.v[\d.]+$/, ""));
  }

  has(archetypeId: string): boolean {
    return this.get(archetypeId) !== undefined;
  }

  listIds(): string[] {
    return [...this.byId.keys()].sort();
  }

  clear(): void {
    this.byId.clear();
    this.templates.clear();
    this.operational.clear();
    this.warnings = [];
    this.loadedPaths = [];
  }
}

export function parseArchetypeFile(text: string): ParseAdlResult {
  return parseAdl(text, { convertAdl14: true });
}

function templateIdOf(template: openehr_am.TEMPLATE): string | undefined {
  return readTemplateId(template);
}

/** `dir/ChemoQ-fatigue.v8.t.json` → `ChemoQ-fatigue.v8`. */
function templateBasename(path: string): string | undefined {
  const name = path.split(/[/\\]/).pop();
  if (!name) return undefined;
  const stem = name.replace(/\.t\.json$/i, "").replace(/\.adls?$/i, "");
  return stem.length > 0 && stem !== name ? stem : undefined;
}
